import React, {useMemo} from 'react';
import {Accordion, Alert, Button, Col, Form, Row} from 'react-bootstrap';
import {toast} from 'react-toastify';
import * as yup from 'yup';
import {useFormik} from "formik";
import homeContentDefaults from "../../content/homeContentDefaults";
import {
    parseSiteContentValues,
    SITE_CONTENT_LIMITS,
    SITE_CONTENT_URL,
    siteContentDraftStore,
    siteContentFreshUrl
} from "../../hooks/useSiteContent";
import useRevisionedContentAdmin, {useUnsavedChangesGuard} from "../../hooks/useRevisionedContentAdmin";
import {SiteContentValues} from "../../models/SiteContent";

// Shared with parseSiteContentValues and the backend's pydantic models.
const LIMITS = SITE_CONTENT_LIMITS;

// Flat form shape: formik's nested error/touched types are painful, and the
// mapping to the API's nested body is trivial.
interface SiteContentFormValues {
    title: string;
    subtitle: string;
    ctaPrimaryLabel: string;
    ctaSecondaryLabel: string;
    stat1Value: string;
    stat1Label: string;
    stat2Value: string;
    stat2Label: string;
    stat3Value: string;
    stat3Label: string;
    announcementText: string;
    announcementEnabled: boolean;
}

const toFormValues = (content: SiteContentValues): SiteContentFormValues => ({
    title: content.hero.title,
    subtitle: content.hero.subtitle,
    ctaPrimaryLabel: content.hero.ctaPrimaryLabel,
    ctaSecondaryLabel: content.hero.ctaSecondaryLabel,
    stat1Value: content.stats[0].value,
    stat1Label: content.stats[0].label,
    stat2Value: content.stats[1].value,
    stat2Label: content.stats[1].label,
    stat3Value: content.stats[2].value,
    stat3Label: content.stats[2].label,
    announcementText: content.announcement.text,
    announcementEnabled: content.announcement.enabled,
});

const toSiteContent = (values: SiteContentFormValues): SiteContentValues => ({
    hero: {
        title: values.title.trim(),
        subtitle: values.subtitle.trim(),
        ctaPrimaryLabel: values.ctaPrimaryLabel.trim(),
        ctaSecondaryLabel: values.ctaSecondaryLabel.trim(),
    },
    stats: [
        {value: values.stat1Value.trim(), label: values.stat1Label.trim()},
        {value: values.stat2Value.trim(), label: values.stat2Label.trim()},
        {value: values.stat3Value.trim(), label: values.stat3Label.trim()},
    ],
    announcement: {text: values.announcementText.trim(), enabled: values.announcementEnabled},
});

const requiredText = (limit: number, label: string) =>
    yup.string()
        .trim()
        .required(`נדרש ${label}`)
        .max(limit, `${label} מוגבל ל-${limit} תווים`);

const siteContentSchema = yup.object().shape({
    title: requiredText(LIMITS.title, 'כותרת'),
    subtitle: requiredText(LIMITS.subtitle, 'תת-כותרת'),
    ctaPrimaryLabel: requiredText(LIMITS.ctaPrimaryLabel, 'טקסט לכפתור הראשי'),
    ctaSecondaryLabel: requiredText(LIMITS.ctaSecondaryLabel, 'טקסט לכפתור המשני'),
    stat1Value: requiredText(LIMITS.statValue, 'ערך'),
    stat1Label: requiredText(LIMITS.statLabel, 'תיאור'),
    stat2Value: requiredText(LIMITS.statValue, 'ערך'),
    stat2Label: requiredText(LIMITS.statLabel, 'תיאור'),
    stat3Value: requiredText(LIMITS.statValue, 'ערך'),
    stat3Label: requiredText(LIMITS.statLabel, 'תיאור'),
    announcementText: requiredText(LIMITS.announcementText, 'טקסט הבאנר'),
});

const AdminSiteContent: React.FC = () => {
    const admin = useRevisionedContentAdmin<SiteContentValues>({
        url: SITE_CONTENT_URL,
        freshUrl: siteContentFreshUrl,
        defaults: homeContentDefaults,
        parseValues: parseSiteContentValues,
        draftStore: siteContentDraftStore,
        previewUrl: token => `/#/?preview=${token}`,
        messages: {
            loadError: 'בעיה בטעינת תוכן דף הבית',
            saveError: 'בעיה בשמירת תוכן דף הבית',
            invalidResponse: 'התקבלה תשובה לא תקינה מהשרת, התוכן לא עודכן במסך',
            conflict: 'התוכן עודכן במקביל — נטען מחדש',
            saveSuccess: 'התוכן נשמר ופורסם בהצלחה',
            previewError: 'יש לתקן את השגיאות בטופס לפני תצוגה מקדימה',
            previewWriteError: 'בעיה ביצירת התצוגה המקדימה',
            stashInfo: key => `הגרסה שניסית לשמור נשמרה מקומית (${key})`,
        },
    });

    const formValues = useMemo(() => toFormValues(admin.values), [admin.values]);

    const formik = useFormik<SiteContentFormValues>({
        initialValues: formValues,
        validationSchema: siteContentSchema,
        onSubmit: values => admin.save(toSiteContent(values)),
        validateOnBlur: true,
        validateOnMount: true,
        enableReinitialize: true,
    });

    useUnsavedChangesGuard(formik.dirty || admin.isSaving);

    const handlePreview = async () => {
        // A preview tab renders whatever it is handed, so an invalid draft must
        // never be written: validate exactly like a save would.
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched(
                Object.keys(formik.values).reduce(
                    (touched, key) => ({...touched, [key]: true}),
                    {} as Record<string, boolean>
                )
            );
            toast.error('יש לתקן את השגיאות בטופס לפני תצוגה מקדימה');
            return;
        }

        admin.openPreview(toSiteContent(formik.values));
    };

    const restoreStashed = () => {
        if (!admin.stashed) {
            return;
        }
        formik.setValues(toFormValues(admin.stashed.values));
        admin.clearStash();
    };

    // the boolean checkbox has its own render path - this helper is strings only
    const field = (name: Exclude<keyof SiteContentFormValues, 'announcementEnabled'>, label: string, limit: number, rows?: number) => {
        const value = formik.values[name];
        const error = formik.touched[name] ? formik.errors[name] : undefined;
        // initialValues is the saved copy (reset on load and after every save),
        // so a difference means "not published yet"
        const changed = value !== formik.initialValues[name];

        return (
            <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="mb-1">
                        {label}
                        {changed && <span className="content-changed-chip">שונה</span>}
                    </Form.Label>
                    <span className={`text-small ${value.length > limit ? 'text-danger' : 'text-gray'}`}>
                        {value.length}/{limit}
                    </span>
                </div>
                {rows ? (
                    <Form.Control
                        as="textarea"
                        rows={rows}
                        name={name}
                        value={value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={!!error}
                        className={changed ? 'content-changed' : undefined}
                    />
                ) : (
                    <Form.Control
                        type="text"
                        name={name}
                        value={value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={!!error}
                        className={changed ? 'content-changed' : undefined}
                    />
                )}
                <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            </Form.Group>
        );
    };

    const statRow = (index: 1 | 2 | 3) => (
        <Row key={index} className="align-items-start">
            <Col xs={12}>
                <div className="fw-bold mb-1">נתון {index}</div>
            </Col>
            <Col md={4}>
                {field(`stat${index}Value` as const, 'ערך', LIMITS.statValue)}
            </Col>
            <Col md={8}>
                {field(`stat${index}Label` as const, 'תיאור', LIMITS.statLabel)}
            </Col>
        </Row>
    );

    return (
        <Row className="justify-content-center py-md-5">
            <Col md={12}>
                <h1 className="mb-4">תוכן דף הבית</h1>
            </Col>
            <Col md={10}>
                {admin.isLoading ? (
                    <div>טוען...</div>
                ) : (
                    <Form noValidate onSubmit={formik.handleSubmit}>
                        {admin.stashed && (
                            <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                                <span>הגרסה שניסית לשמור נשמרה מקומית ולא אבדה.</span>
                                <span>
                                    <Button variant="outline-dark" size="sm" className="me-2" type="button"
                                            onClick={restoreStashed}>
                                        שחזור העריכה שלי
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" type="button"
                                            onClick={admin.clearStash}>
                                        התעלמות
                                    </Button>
                                </span>
                            </Alert>
                        )}

                        <Accordion defaultActiveKey={['hero', 'stats', 'announcement']} alwaysOpen>
                            <Accordion.Item eventKey="hero">
                                <Accordion.Header>הירו</Accordion.Header>
                                <Accordion.Body>
                                    {field('title', 'כותרת ראשית', LIMITS.title, 2)}
                                    <div className="text-small text-gray mb-3">
                                        כל שורה אחרי הראשונה מוצגת בצבע ההדגשה.
                                    </div>
                                    {field('subtitle', 'תת-כותרת', LIMITS.subtitle, 3)}
                                    {field('ctaPrimaryLabel', 'כפתור ראשי', LIMITS.ctaPrimaryLabel)}
                                    {field('ctaSecondaryLabel', 'כפתור משני', LIMITS.ctaSecondaryLabel)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="stats">
                                <Accordion.Header>נתונים</Accordion.Header>
                                <Accordion.Body>
                                    {statRow(1)}
                                    {statRow(2)}
                                    {statRow(3)}
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="announcement">
                                <Accordion.Header>באנר עליון</Accordion.Header>
                                <Accordion.Body>
                                    <div className="text-small text-gray mb-3">
                                        הבאנר מוצג בראש האתר רק כאשר "מוצג באתר" מסומן. ללא סימון התוכן נשמר אך לא מוצג.
                                    </div>
                                    <Form.Group className="mb-3 d-flex align-items-center">
                                        <Form.Check
                                            type="checkbox"
                                            id="announcementEnabled"
                                            name="announcementEnabled"
                                            label="מוצג באתר"
                                            checked={formik.values.announcementEnabled}
                                            onChange={formik.handleChange}
                                        />
                                        {formik.values.announcementEnabled !== formik.initialValues.announcementEnabled
                                            && <span className="content-changed-chip">שונה</span>}
                                    </Form.Group>
                                    {field('announcementText', 'טקסט הבאנר', LIMITS.announcementText, 2)}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        <div className="d-flex gap-2 mt-4">
                            <Button variant="primary" type="submit" disabled={admin.isSaving}>
                                שמירה ופרסום
                            </Button>
                            <Button variant="outline-secondary" type="button" onClick={handlePreview}>
                                תצוגה מקדימה
                            </Button>
                        </div>
                    </Form>
                )}
            </Col>
        </Row>
    );
};

export default AdminSiteContent;
